import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { bool, string } from 'prop-types';
import { intlShape, injectIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import { NamedLink, ResponsiveImage } from '..';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { ensureListing, ensureUser } from '../../util/data';
import { propTypes } from '../../util/types';
import { convertNumberToMoney, priceData } from '../../util/currency';
import { LISTING_TYPE } from '../../constants/listingAttributes';
import SaveListingButton from '../SaveListingButton/SaveListingButton';
import { getCustomerFee, getNormalizedFee } from '../../util/sessions';
import { CUSTOMER_FEE_PERCENTAGE } from '../../transactions/transactionsUtil';
import { useHourlyDuration} from '../../hooks/useHourlyDuration';

import css from './ListingCardCompact.module.css';
import { createURLParamsForListing } from '../../util/listingHelpers';

const ListingImage = props => <ResponsiveImage {...props} />;
const LazyImage = lazyLoadWithDimensions(ListingImage, { loadAfterInitialRendering: 3000 });

const getPriceWithFee = (price, feeAmount) => {
  if (!price) return;

  return convertNumberToMoney(price.amount + feeAmount, price.currency);
};

const ListingCardCompactComponent = props => {
  const {
    imageWrapperClassName,
    listing,
    showActivityLocation,
    showPrice,
    showDuration,
    showSave,
    isHorizontal,
    intl,
    currentUser,
    interfaceLang,
  } = props;

const { id, attributes, images, author } = listing;
const { title: listingTitle = '', price, publicData, deleted } = attributes;
const { discipline, groupType, duration, activity, multiLangAddressParts, type, advancedAvailabilityPlan } = publicData || {};
const firstImage = useMemo(() => (images?.length > 0 ? images[0] : null), [images]);

// Get address parts for current language
const getLanguageKey = (lang) => {
  switch (lang) {
    case 'en':
      return 'english';
    case 'fr':
      return 'french';
    case 'de':
      return 'german';
    case 'it':
      return 'italian';
    default:
      return 'english'; // Fallback to English
  }
};

// Get current language from locale
// interfaceLang from Redux is reliable on SSR; intl.locale defaults to 'en-US' on SSR
const currentLanguage = (interfaceLang || intl.locale).split('-')[0]; // 'fr' from 'fr-FR'
const addressParts = multiLangAddressParts?.[getLanguageKey(currentLanguage)];

  const { entries } = advancedAvailabilityPlan || {};
  const hourlyDuration = useHourlyDuration(duration, entries);

  const ensuredAuthor = useMemo(() => ensureUser(author), [author]);

  const isFree = useMemo(() => type === LISTING_TYPE.FREE, [type]);

  const isCurrentUserAuthor = currentUser?.id?.uuid === ensuredAuthor.id?.uuid;

  const minPriceBase = convertNumberToMoney(
    advancedAvailabilityPlan.entries.reduce((minAmount, entry) => {
      if (!duration) return minAmount;

      const amountWithDuration = entry.price * duration;

      return Math.min(minAmount, amountWithDuration);
    }, price?.amount),
    price?.currency
  );

  const buyerFee = isCurrentUserAuthor ? CUSTOMER_FEE_PERCENTAGE : getCustomerFee(currentUser);

  const feeAmount =
    minPriceBase?.amount * (getNormalizedFee(buyerFee, CUSTOMER_FEE_PERCENTAGE) / 100);

  const minPrice = getPriceWithFee(minPriceBase, feeAmount);

  const { formattedPrice } = useMemo(() => priceData(minPrice, intl), [intl, minPrice]);

  const messages = useMemo(
    () => ({
      activity: activity && intl.formatMessage({ id: `General.${activity}` }),
      discipline: discipline && intl.formatMessage({ id: `General.${discipline}` }),
      location: addressParts ? addressParts.country : null,
      duration: duration > 1
        ? intl.formatMessage({ id: 'ListingPage.days' }, { duration })
        : hourlyDuration
        ? intl.formatMessage({ id: 'BookingBreakdown.quantity' }, { quantity: hourlyDuration })
        : null,
      price: isFree 
        ? intl.formatMessage({ id: 'General.free' })
        : intl.formatMessage(
            { id: 'General.priceFrom' },
            { formattedPrice }
          ),
      priceType: groupType === 'group'
        ? intl.formatMessage({ id: 'General.perPerson' })
        : intl.formatMessage({ id: 'General.perSession' })
    }),
    [activity, intl, discipline, addressParts, duration, hourlyDuration, formattedPrice, isFree, groupType]
  );

  //JSX statement
  return (
    <>
      <div className={classNames(css.imageWrapper, { [css.imageWrapperHorizontal]: isHorizontal }, imageWrapperClassName)}>
        <div className={css.aspectWrapper}>
          <LazyImage
            rootClassName={css.rootForImage}
            alt={listingTitle || ''}
            image={firstImage}
            variants={['landscape-crop', 'landscape-crop2x']}
          />
          {showSave && !deleted && (
            <SaveListingButton rootClassName={css.heartButton} listingId={id.uuid} />
          )}
        </div>
      </div>

      <div className={css.infoWrapper}>
        <div>
          <div className={css.activityName}>{messages.discipline}</div>
          {showActivityLocation && messages.location && (
            <div className={css.activityPlaceName}>{messages.location}</div>
          )}
        </div>
        <div className={css.bottomWrapper}>
          <div className={css.priceSection}>
            <div className={css.durationPriceWrapper}>
              {showDuration && messages.duration && (
                <span className={css.duration}>{messages.duration}</span>
              )}
              {showPrice && (
                <span className={css.price}>
                  <span className={css.activeText}>{messages.price}</span>
                </span>
              )}
            </div>
            {showPrice && (
              <div className={css.priceType}>{messages.priceType}</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

ListingCardCompactComponent.defaultProps = {
  rootClassName: null,
  className: null,
  imageWrapperClassName: null,
  showActivityLocation: true,
  showPrice: true,
  showDuration: true,
  showSave: true,
  isHorizontal: false,
};

ListingCardCompactComponent.propTypes = {
  rootClassName: string,
  className: string,
  imageWrapperClassName: string,
  listing: propTypes.listing.isRequired,
  showActivityLocation: bool,
  showPrice: bool,
  showDuration: bool,
  isHorizontal: bool,

  intl: intlShape.isRequired,
};

const ListingCardCompact = props => {
  const { rootClassName, className, listing, isHorizontal, ...rest } = props;
  const interfaceLang = useSelector(state => state.ui.interfaceLang);
  const classes = useMemo(
    () => classNames(rootClassName || css.root, { [css.horizonalCard]: isHorizontal }, className),
    [className, isHorizontal, rootClassName]
  );
  const ensuredListing = useMemo(() => ensureListing(listing), [listing]);
  const { attributes } = ensuredListing;

  const { deleted } = attributes;

  const namedLinkProps = useMemo(
    () =>
      deleted ? {} : { name: 'ListingPage', params: createURLParamsForListing(ensuredListing, interfaceLang, props.intl) },
    [deleted, ensuredListing, interfaceLang]
  );

  return deleted ? (
    <div className={classes}>
      <ListingCardCompactComponent listing={ensuredListing} isHorizontal={isHorizontal} interfaceLang={interfaceLang} {...rest} />
    </div>
  ) : (
    <NamedLink {...namedLinkProps} className={classes} target="_blank">
      <ListingCardCompactComponent listing={ensuredListing} isHorizontal={isHorizontal} interfaceLang={interfaceLang} {...rest} />
    </NamedLink>
  );
};
ListingCardCompact.displayName = 'ListingCardCompact';
export default injectIntl(ListingCardCompact);

// NamedLink component cannot be used outside of Router
// Some components, such as SearchMap, are global scoped
// Redux store is also not available (so we cannot use SaveListingButton)
const ListingCardCompactWithoutLinkComponent = props => {
  const { rootClassName, className, listing, ...rest } = props;
  const classes = useMemo(() => classNames(rootClassName || css.root, className), [
    className,
    rootClassName,
  ]);
  const ensuredListing = useMemo(() => ensureListing(listing), [listing]);

  return (
    <div className={classes}>
      <ListingCardCompactComponent listing={ensuredListing} showSave={false} {...rest} />
    </div>
  );
};
ListingCardCompactWithoutLinkComponent.displayName = 'ListingCardCompactWithoutLinkComponent';
export const ListingCardCompactWithoutLink = injectIntl(ListingCardCompactWithoutLinkComponent);
